import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { URL } from "../store/actions/depannageActions"
import {
	clearFicheDemande,
	clearFicheDemandeReponse,
	clearFichePrise,
	postFicheOuverte,
} from "../store/actions/ficheOuverteActions"
import { getOpenerId } from "../utilities/session"
import { useGroups } from "./groups"
import {
	DemandeMainModal,
	DemandeRecueModal,
	MainPerdueModal,
} from "../components/others/FicheVerrou"

// Verrou exclusif d'une fiche — mécanique partagée par les trois fiches
// (dépannage, atelier, location). Elle est née dans AtelierV2, seul module
// verrouillé jusqu'ici ; le backend (`FicheOuverte` : `fiche_id` + `type`) et
// les notifications WS étaient déjà génériques, il n'y avait que le front à
// factoriser.
//
// Règle : le PREMIER à ouvrir `/module/fiche/:id` détient le verrou et peut
// éditer. Les sessions suivantes voient la fiche en lecture seule et peuvent
// demander la main (le détenteur accepte ou refuse ; admin/boss peuvent
// forcer). Le verrou est par SESSION (onglet), pas par utilisateur : un 2e
// onglet du même user est aussi en lecture seule.
//
// Heartbeat toutes les 3 min (TTL serveur 10 min contre les verrous fantômes),
// libération au démontage / changement de fiche / fermeture d'onglet.

const HEARTBEAT_MS = 3 * 60 * 1000
// Le serveur purge une demande sans réponse à 45 s : on abandonne avant, pour
// afficher « pas de réponse » plutôt que de laisser expirer en silence.
const DEMANDE_TIMEOUT_MS = 30000

// Détenteur du verrou d'une fiche, vu de CETTE session : l'entrée du verrou si
// elle appartient à une AUTRE session, sinon null (verrou à nous, ou libre).
// Exporté : `useAtelierPermissions` en dérive aussi son `canEdit`, la règle
// doit rester unique.
export function detenteurFiche(fichesOuvertes, type, ficheId, openerId) {
	if (!ficheId) return null
	return (
		(fichesOuvertes || []).find(
			(f) =>
				f.fiche_id === ficheId &&
				f.type === type &&
				f.opener !== openerId,
		) || null
	)
}

/**
 * @param type      "dep" | "ate" | "loc"
 * @param ficheId   id de la fiche (URL ou slice) — 0/null/"0" = fiche neuve,
 *                  aucun verrou serveur n'est posé.
 * @param options.onMainObtenue      appelé quand la main est (re)prise → la
 *                                   fiche doit se RECHARGER (on était en
 *                                   lecture seule, nos données sont périmées).
 *                                   Un seul point d'appel : le passage
 *                                   verrouillée → libre (voir plus bas).
 * @param options.travailNonEnregistre  booléen : prévient le détenteur qu'il
 *                                   perdra sa saisie s'il cède la main.
 * @returns { verrouillee, detenuePar, demanderLaMain, VerrouUI }
 */
export default function useFicheVerrou(
	type,
	ficheId,
	{ onMainObtenue = null, travailNonEnregistre = false } = {},
) {
	const dispatch = useDispatch()
	const token = useSelector((state) => state.navInfo.login.token)
	const fichesOuvertes = useSelector(
		(state) => state.fichesOuvertesInfo.fichesOuvertes,
	)
	const fichePrise = useSelector((state) => state.fichesOuvertesInfo.prise)
	const ficheDemande = useSelector(
		(state) => state.fichesOuvertesInfo.demande,
	)
	const ficheDemandeReponse = useSelector(
		(state) => state.fichesOuvertesInfo.demandeReponse,
	)
	const { isBoss, isAdmin } = useGroups()
	// Secours réservé aux administrateurs : kicke le détenteur sans son accord.
	const canForce = isBoss || isAdmin

	const id = parseInt(ficheId)
	const ficheValide = Number.isInteger(id) && id > 0

	// Cycle du modal de demande, côté demandeur.
	const [demandePhase, setDemandePhase] = useState(null)
	const [requesting, setRequesting] = useState(false)
	const [takingOver, setTakingOver] = useState(false)
	const [respondingDemande, setRespondingDemande] = useState(false)
	const demandeTimerRef = useRef(null)
	// `onMainObtenue` est recréé à chaque rendu par les fiches : on le lit via
	// une ref pour ne pas relancer l'effet d'écoute à chaque frappe.
	const onMainObtenueRef = useRef(onMainObtenue)
	useEffect(() => {
		onMainObtenueRef.current = onMainObtenue
	})

	const openerId = getOpenerId()
	const detenteur = useMemo(
		() =>
			detenteurFiche(
				fichesOuvertes,
				type,
				ficheValide ? id : 0,
				openerId,
			),
		[fichesOuvertes, type, id, ficheValide, openerId],
	)
	const verrouillee = detenteur !== null
	const detenuePar = detenteur ? detenteur.user : ""

	// --- Main (re)prise → rechargement -------------------------------------
	// Toutes les façons de récupérer la main finissent par le même signal : la
	// liste des verrous ne montre plus d'autre détenteur. Demande acceptée
	// (`fiche_demande_reponse` relit la liste), main libre au moment de la
	// demande ou prise forcée (réponse du POST), et verrou de l'autre expiré
	// (TTL 10 min) que notre heartbeat récupère : ce dernier cas ne passait par
	// aucun callback, la fiche repassait en édition sur des données périmées
	// (QA 12.10). On recharge donc sur la TRANSITION, une seule fois.
	// Pas au premier montage ni au changement de fiche : la fiche charge déjà
	// ses données, et la liste du store peut dater d'avant notre `open` (ex.
	// liste des résultats) — on n'arme qu'une fois la réponse de notre `open`
	// reçue pour CETTE fiche.
	const cleVerrou = ficheValide ? `${type}:${id}` : null
	const transitionRef = useRef({
		cle: null,
		verrouillee: false,
		armee: false,
	})
	useEffect(() => {
		const prec = transitionRef.current
		if (prec.cle !== cleVerrou) {
			transitionRef.current = {
				cle: cleVerrou,
				verrouillee,
				armee: false,
			}
			return
		}
		if (prec.armee && prec.verrouillee && !verrouillee) {
			if (onMainObtenueRef.current) onMainObtenueRef.current()
		}
		prec.verrouillee = verrouillee
	}, [cleVerrou, verrouillee])

	const clearDemandeTimer = useCallback(() => {
		if (demandeTimerRef.current) {
			clearTimeout(demandeTimerRef.current)
			demandeTimerRef.current = null
		}
	}, [])

	// --- Cycle de vie du verrou -------------------------------------------
	useEffect(() => {
		// Fiche neuve (id 0 / -1) : rien à verrouiller côté serveur. Après le
		// save, le `history.replace` vers l'id réel repasse ici.
		if (!ficheValide) return
		// Notif/demande d'une AUTRE fiche : périmées, on nettoie.
		dispatch(clearFichePrise())
		dispatch(clearFicheDemande())
		dispatch(clearFicheDemandeReponse())
		const cle = `${type}:${id}`
		dispatch(postFicheOuverte({ fiche_id: id, type, action: "open" })).then(
			(res) => {
				// On arme en partant de l'état que DIT cette réponse : l'effet
				// de transition peut passer avant ou après ce `then`, le
				// résultat ne doit pas en dépendre.
				const t = transitionRef.current
				if (t.cle !== cle) return
				if (Array.isArray(res))
					t.verrouillee = !!detenteurFiche(
						res,
						type,
						id,
						getOpenerId(),
					)
				t.armee = true
			},
		)
		const heartbeat = setInterval(() => {
			dispatch(postFicheOuverte({ fiche_id: id, type, action: "open" }))
		}, HEARTBEAT_MS)
		// Libération best-effort si l'onglet se ferme (keepalive autorise les headers).
		const releaseBeacon = () => {
			try {
				fetch(`${URL}api/ficheouverte`, {
					method: "post",
					keepalive: true,
					headers: {
						"Content-type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						fiche_id: id,
						type,
						action: "close",
						opener: getOpenerId(),
					}),
				})
			} catch (e) {
				/* best-effort */
			}
		}
		window.addEventListener("beforeunload", releaseBeacon)
		return () => {
			clearInterval(heartbeat)
			window.removeEventListener("beforeunload", releaseBeacon)
			dispatch(postFicheOuverte({ fiche_id: id, type, action: "close" }))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, type, ficheValide])

	// Nettoyage du timer de demande au démontage.
	useEffect(() => () => clearDemandeTimer(), [clearDemandeTimer])

	// --- Demandeur : réception de la réponse du détenteur ------------------
	useEffect(() => {
		if (!ficheDemandeReponse || !ficheValide) return
		if (
			ficheDemandeReponse.type !== type ||
			ficheDemandeReponse.fiche_id !== id
		)
			return
		clearDemandeTimer()
		if (ficheDemandeReponse.accepted) {
			// Rechargement : par la transition, une fois la liste relue.
			setDemandePhase(null)
		} else {
			setDemandePhase("refused")
		}
		dispatch(clearFicheDemandeReponse())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ficheDemandeReponse, id, type, ficheValide])

	// --- Actions -----------------------------------------------------------

	// Demandeur : envoie la demande puis attend la réponse (30 s max).
	const requestFiche = () => {
		setRequesting(true)
		dispatch(postFicheOuverte({ fiche_id: id, type, action: "request" }))
			.then((res) => {
				// Échec (ex. 409 : une demande est déjà en cours) → on referme.
				if (!res) {
					setDemandePhase(null)
					return
				}
				// Fiche libérée entre-temps : le bandeau « verrouillée par X »
				// pouvait être périmé (détenteur parti depuis moins d'un
				// heartbeat, ou verrou fantôme purgé par le TTL). Dans ce cas le
				// serveur nous attribue le verrou dans la foulée du `request`
				// (branche `elif not fiche.id`) — la liste renvoyée par le POST
				// le dit. Sans ce test on attendait une réponse qui ne viendrait
				// jamais : 30 s de spinner puis un faux « X n'a pas répondu »,
				// alors que la main était acquise.
				if (
					Array.isArray(res) &&
					!detenteurFiche(res, type, id, getOpenerId())
				) {
					// La réponse du POST est déjà dans le store : la
					// transition verrouillée → libre recharge la fiche.
					clearDemandeTimer()
					setDemandePhase(null)
					return
				}
				setDemandePhase("pending")
				clearDemandeTimer()
				demandeTimerRef.current = setTimeout(() => {
					dispatch(
						postFicheOuverte({
							fiche_id: id,
							type,
							action: "cancel_request",
						}),
					)
					setDemandePhase("timeout")
				}, DEMANDE_TIMEOUT_MS)
			})
			.finally(() => setRequesting(false))
	}

	const cancelDemande = () => {
		clearDemandeTimer()
		dispatch(
			postFicheOuverte({ fiche_id: id, type, action: "cancel_request" }),
		)
		setDemandePhase(null)
	}

	const closeDemande = () => {
		clearDemandeTimer()
		setDemandePhase(null)
	}

	// Détenteur : accepter cède le verrou, refuser le garde.
	const respondDemande = (accept) => {
		setRespondingDemande(true)
		dispatch(
			postFicheOuverte({
				fiche_id: id,
				type,
				action: accept ? "accept_request" : "refuse_request",
			}),
		).finally(() => {
			setRespondingDemande(false)
			dispatch(clearFicheDemande())
		})
	}

	// Secours admin/boss : prise de main forcée (le détenteur est kické).
	const forceOpenFiche = () => {
		clearDemandeTimer()
		setTakingOver(true)
		// Rechargement : par la transition (la réponse du POST relit la liste).
		dispatch(
			postFicheOuverte({ fiche_id: id, type, action: "force_open" }),
		).finally(() => {
			setTakingOver(false)
			setDemandePhase(null)
		})
	}

	// Ouvre le cycle « demander la main » (bouton cadenas de la fiche).
	const demanderLaMain = () => setDemandePhase("confirm")

	const demandeRecue =
		!!ficheDemande &&
		ficheValide &&
		ficheDemande.type === type &&
		ficheDemande.fiche_id === id
	const mainPerdue =
		!!fichePrise &&
		ficheValide &&
		fichePrise.type === type &&
		fichePrise.fiche_id === id
	// Vrai dès qu'un modal du verrou est à l'écran : les fiches s'en servent
	// pour neutraliser leurs raccourcis clavier (Échap, F11/F12…).
	const modalOuvert = !!demandePhase || demandeRecue || mainPerdue

	const VerrouUI = (
		<>
			{demandePhase && (
				<DemandeMainModal
					phase={demandePhase}
					holder={detenuePar}
					canForce={canForce}
					busy={requesting || takingOver}
					onRequest={requestFiche}
					onCancel={cancelDemande}
					onForce={forceOpenFiche}
					onClose={closeDemande}
				/>
			)}
			{demandeRecue && (
				<DemandeRecueModal
					by={ficheDemande.by}
					hasUnsavedChanges={travailNonEnregistre}
					pending={respondingDemande}
					onAccept={() => respondDemande(true)}
					onRefuse={() => respondDemande(false)}
				/>
			)}
			{mainPerdue && (
				<MainPerdueModal
					by={fichePrise.by}
					onClose={() => dispatch(clearFichePrise())}
				/>
			)}
		</>
	)

	return { verrouillee, detenuePar, demanderLaMain, modalOuvert, VerrouUI }
}
